import type { ComponentProps, ReactElement } from "react";
import { XIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle as DialogTitlePrimitive,
} from "@/components/ui/dialog";
import { headingVariantClassName } from "@/components/ui/heading";
import { cn } from "@/lib/utils";

/** Edge-to-edge full viewport below `lg` (phones/tablets). */
const modalMobileShellClassName =
  "max-lg:!top-0 max-lg:!right-0 max-lg:!bottom-0 max-lg:!left-0 max-lg:!h-dvh max-lg:!max-h-dvh max-lg:!w-full max-lg:!max-w-none max-lg:!translate-x-0 max-lg:!translate-y-0 max-lg:!rounded-none max-lg:ring-0 max-lg:min-h-0 max-lg:overflow-hidden";

/**
 * Body fills leftover height only inside the fixed `h-dvh` shell.
 * On compact shells, `flex-1` + `basis-0` collapses the body when parent height is content-sized.
 */
const modalMobileFullscreenBodyFillClassName =
  "max-lg:[&_[data-slot=modal-body]]:min-h-0 max-lg:[&_[data-slot=modal-body]]:flex-1 max-lg:[&_[data-slot=modal-body]]:basis-0 max-lg:[&_[data-slot=modal-body]]:overflow-x-hidden max-lg:[&_[data-slot=modal-body]]:overflow-y-auto max-lg:[&_[data-slot=modal-body]]:overscroll-y-contain max-lg:[&_[data-slot=modal-body]]:touch-pan-y";

/** Centered modal panel. Built on {@link Dialog} with shared layout defaults. */
function Modal(props: ComponentProps<typeof Dialog>): ReactElement {
  return <Dialog {...props} />;
}

/** Compact centered shell for short dialogs (e.g. confirmations) on mobile. */
const modalMobileCompactClassName =
  "max-lg:max-h-[calc(100dvh-2rem)] max-lg:overflow-x-hidden max-lg:overflow-y-auto max-lg:overscroll-y-contain";

type ModalContentProps = ComponentProps<typeof DialogContent> & {
  /**
   * Full viewport shell on mobile (`max-lg`). Defaults to `true` for form modals.
   * Set `false` for short dialogs (e.g. confirmations) to keep a compact centered box.
   */
  mobileFullscreen?: boolean;
  /**
   * When `false`, overlay click does not close (action / confirm / form modals).
   * Close with Cancel, X, or the primary action. Defaults to `false`.
   * Set `true` for viewers (PDF preview, document preview).
   */
  dismissOnOutside?: boolean;
};

/**
 * Modal shell.
 * - Mobile (`max-lg`): full viewport flex column; body scrolls between header and footer.
 * - Desktop (`lg+`): centered auto-height panel; body scrolls only when content exceeds cap.
 */
function ModalContent({
  className,
  showCloseButton = false,
  mobileFullscreen = true,
  dismissOnOutside = false,
  onPointerDownOutside,
  onFocusOutside,
  onInteractOutside,
  ...props
}: ModalContentProps): ReactElement {
  return (
    <DialogContent
      showCloseButton={showCloseButton}
      className={cn(
        // DialogContent defaults to `grid`; use flex column for header/body/footer layout.
        "!flex w-full flex-col gap-0 bg-background p-0 text-foreground shadow-lg",
        mobileFullscreen
          ? cn(modalMobileShellClassName, modalMobileFullscreenBodyFillClassName)
          : modalMobileCompactClassName,
        "lg:h-auto lg:max-h-none lg:overflow-visible lg:max-w-lg",
        className,
      )}
      {...props}
      onPointerDownOutside={(event) => {
        if (!dismissOnOutside) event.preventDefault();
        onPointerDownOutside?.(event);
      }}
      onFocusOutside={(event) => {
        if (!dismissOnOutside) event.preventDefault();
        onFocusOutside?.(event);
      }}
      onInteractOutside={(event) => {
        if (!dismissOnOutside) event.preventDefault();
        onInteractOutside?.(event);
      }}
    />
  );
}

type ModalBodyProps = ComponentProps<"div">;

/** Scrollable modal main content with standard horizontal padding. */
function ModalBody({ className, ...props }: ModalBodyProps): ReactElement {
  return (
    <div
      data-slot="modal-body"
      className={cn(
        "min-w-0 overflow-x-hidden px-6 py-5 sm:py-6",
        // Desktop: grow with content; scroll inside body when tall.
        "lg:max-h-[min(65dvh,calc(100dvh-12rem))] lg:overflow-y-auto",
        className,
      )}
      {...props}
    />
  );
}

type ModalHeaderProps = ComponentProps<"div"> & {
  showClose?: boolean;
  /** Keep the close control visible but unclickable (e.g. while a mutation is pending). */
  closeDisabled?: boolean;
  closeLabel?: string;
  /** Preferred for controlled modals — avoids Radix Close context edge cases. */
  onClose?: () => void;
};

function ModalHeader({
  className,
  children,
  showClose = true,
  closeDisabled = false,
  closeLabel = "Close",
  onClose,
  ...props
}: ModalHeaderProps): ReactElement {
  const closeButton = (
    <Button
      type="button"
      variant="ghost"
      size="icon-sm"
      className="shrink-0"
      disabled={closeDisabled}
      onClick={onClose}
    >
      <XIcon className="size-4" aria-hidden />
      <span className="sr-only">{closeLabel}</span>
    </Button>
  );

  return (
    <div
      data-slot="modal-header"
      className={cn(
        "flex shrink-0 flex-row items-start gap-3 border-b border-border px-6 py-4 sm:py-5",
        className,
      )}
      {...props}
    >
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">{children}</div>
      {showClose ? onClose ? closeButton : <DialogClose asChild>{closeButton}</DialogClose> : null}
    </div>
  );
}

function ModalFooter({ className, ...props }: ComponentProps<"div">): ReactElement {
  return (
    <div
      data-slot="modal-footer"
      className={cn(
        "mt-auto flex shrink-0 flex-row gap-2 border-t border-border px-6 py-4 sm:justify-end sm:py-5 max-lg:py-4 max-lg:pb-[max(1rem,env(safe-area-inset-bottom))] max-lg:[&_[data-slot=button]]:h-10 max-sm:[&_[data-slot=button]]:min-w-0 max-sm:[&_[data-slot=button]]:flex-1",
        className,
      )}
      {...props}
    />
  );
}

const ModalClose = DialogClose;
const ModalDescription = DialogDescription;

function ModalTitle({
  className,
  ...props
}: ComponentProps<typeof DialogTitlePrimitive>): ReactElement {
  return (
    <DialogTitlePrimitive
      className={cn(headingVariantClassName.section, "leading-tight sm:text-xl", className)}
      {...props}
    />
  );
}

export {
  Modal,
  ModalBody,
  ModalClose,
  ModalContent,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalTitle,
};
